/**
 * Goanna IM Presenter — shared data model.
 *
 * Zod schemas are the single source of truth; TypeScript types are inferred from
 * them so the wire format, the client and the server can never drift apart.
 * Every real-time payload is validated against these on receipt.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export const RoleSchema = z.enum(['presenter', 'producer']);
// ---------------------------------------------------------------------------
// Script / project model
// ---------------------------------------------------------------------------
/**
 * A block is the smallest reading unit. The producer edits plain text, but it is
 * parsed into structured blocks so we can anchor the reading position to a block
 * (survives live edits) and later grow into full run-sheets.
 */
export const BlockTypeSchema = z.enum(['paragraph', 'question', 'direction', 'marker']);
export const BlockSchema = z.object({
    id: z.string().min(1),
    type: BlockTypeSchema,
    text: z.string(),
    speaker: z.string().optional(),
    estDurationSec: z.number().nonnegative().optional(),
    notes: z.string().optional(),
});
export const SectionSchema = z.object({
    id: z.string().min(1),
    heading: z.string(),
    blocks: z.array(BlockSchema),
});
export const ProjectSchema = z.object({
    id: z.string().min(1),
    title: z.string(),
    description: z.string().default(''),
    sections: z.array(SectionSchema),
    version: z.number().int().nonnegative().default(1),
    createdAt: z.number().int(),
    modifiedAt: z.number().int(),
});
// ---------------------------------------------------------------------------
// Information zone — cues / messages
// ---------------------------------------------------------------------------
export const InfoMessageTypeSchema = z.enum([
    'message', // general producer message
    'question', // next question to ask
    'fact', // hot fact
    'cue', // production cue (camera, video, etc.)
    'timer', // timing information
    'urgent', // urgent alert — may take over more of the display
    'ai', // AI suggestion (must be producer-approved before it appears)
]);
export const InfoMessageSchema = z.object({
    id: z.string().min(1),
    type: InfoMessageTypeSchema,
    heading: z.string().default(''),
    body: z.string(),
    priority: z.number().int().min(0).max(3).default(1),
    createdAt: z.number().int(),
    /** Absolute epoch ms after which the message auto-clears. Undefined = sticky. */
    expiresAt: z.number().int().optional(),
    /** Optional emphasis category, e.g. 'red', 'amber', 'blue'. Presentation only. */
    colour: z.string().optional(),
    vibrate: z.boolean().optional(),
    requireAck: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export const LayoutPresetIdSchema = z.enum([
    'standard', // ~33% info / 67% script
    'scriptPriority', // ~15% / 85%
    'interview', // ~40% / 60%
    'split', // 50% / 50%
    'scriptOnly', // info hidden
    'alertTakeover', // info dominates (transient)
]);
export const LayoutStateSchema = z.object({
    presetId: LayoutPresetIdSchema,
    /** Fraction of the display height given to the info zone, 0..1. Never hard-coded. */
    infoRatio: z.number().min(0).max(1),
});
// ---------------------------------------------------------------------------
// Presenter display settings (remotely controllable)
// ---------------------------------------------------------------------------
export const DisplaySettingsSchema = z.object({
    fontSizePx: z.number().min(12).max(200).default(56),
    lineHeight: z.number().min(0.8).max(3).default(1.4),
    fontFamily: z
        .enum(['system', 'serif', 'mono', 'humanist', 'condensed'])
        .default('humanist'),
    align: z.enum(['left', 'center', 'right']).default('left'),
    uppercase: z.boolean().default(false),
    marginLeftPct: z.number().min(0).max(45).default(8),
    marginRightPct: z.number().min(0).max(45).default(8),
    /** Vertical position of the reading band as a fraction of the script zone, 0..1. */
    readingBandPos: z.number().min(0).max(1).default(0.4),
    showReadingLine: z.boolean().default(true),
    fadeTopPct: z.number().min(0).max(40).default(12),
    fadeBottomPct: z.number().min(0).max(40).default(12),
    mirrorHorizontal: z.boolean().default(true),
    mirrorVertical: z.boolean().default(false),
    contrast: z.enum(['standard', 'high', 'soft']).default('standard'),
    brightnessFriendly: z.boolean().default(false),
    showProgress: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// Scroll state — authoritative, time-based, frame-rate independent
// ---------------------------------------------------------------------------
/**
 * The reading position is anchored to a block (not a raw pixel) so live edits and
 * font/layout changes preserve it. `offsetFraction` is 0..1 within that block.
 */
export const ScrollAnchorSchema = z.object({
    blockId: z.string(),
    offsetFraction: z.number().min(0).max(1).default(0),
    /** Epoch ms at which this anchor was true — used to interpolate while running. */
    atTime: z.number().int(),
});
export const ScrollStateSchema = z.object({
    running: z.boolean().default(false),
    speedPxPerSec: z.number().min(0).max(2000).default(60),
    anchor: ScrollAnchorSchema,
});
// ---------------------------------------------------------------------------
// Timers
// ---------------------------------------------------------------------------
export const TimerSchema = z.object({
    mode: z.enum(['countUp', 'countDown']),
    running: z.boolean().default(false),
    /** For countUp: elapsed ms baseline. For countDown: remaining ms baseline. */
    baseMs: z.number().int().nonnegative().default(0),
    /** Epoch ms the timer was last started; null when paused. */
    startedAt: z.number().int().nullable().default(null),
    /** countDown total, ms. */
    durationMs: z.number().int().nonnegative().default(0),
    warnMs: z.number().int().nonnegative().default(60_000),
    urgentMs: z.number().int().nonnegative().default(15_000),
    visible: z.boolean().default(false),
});
export const TimersSchema = z.object({
    production: TimerSchema, // count-up show timer
    segment: TimerSchema, // count-down segment timer
});
// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------
export const PresenceSchema = z.object({
    socketId: z.string(),
    role: RoleSchema,
    name: z.string().default(''),
    lastSeen: z.number().int(),
    /** Capability-detected only; absent when the device/browser does not expose it. */
    batteryPct: z.number().min(0).max(100).optional(),
    online: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Full session state (authoritative on the server; snapshot sent to clients)
// ---------------------------------------------------------------------------
export const SessionStateSchema = z.object({
    sessionId: z.string(),
    roomCode: z.string(),
    /** Epoch ms the session was created. */
    createdAt: z.number().int().default(0),
    /**
     * Epoch ms after which a presenter may no longer join/read the script — the
     * producer's script is only exposed to talent inside this window (default 12h).
     * **0 = the window has not started.** It begins only when the first presenter
     * device connects (set server-side to now + 12h), so a script can be prepared
     * days ahead. The producer/admin are never locked out; a presenter already
     * connected keeps running, but a fresh load or reconnect after expiry is refused.
     */
    expiresAt: z.number().int().default(0),
    /**
     * Whether the producer has opted this show IN to transcript recording. Off by
     * default; only the producer (or an assisting admin) can turn it on, before or
     * during the show. When on, the presenter captures a timestamped transcript.
     */
    recording: z.boolean().default(false),
    project: ProjectSchema,
    scroll: ScrollStateSchema,
    layout: LayoutStateSchema,
    settings: DisplaySettingsSchema,
    message: InfoMessageSchema.nullable(),
    timers: TimersSchema,
    presence: z.array(PresenceSchema),
});
/** The presenter access window: how long talent may read a producer's script. */
export const PRESENTER_ACCESS_WINDOW_MS = 12 * 60 * 60 * 1000;
/** Whether the 12-hour presenter window has begun (a device has connected). */
export function presenterWindowStarted(state) {
    return !!state.expiresAt;
}
/** True while a presenter is still allowed to join/read the session's script. */
export function presenterAccessOpen(state, nowMs) {
    return !state.expiresAt || nowMs < state.expiresAt;
}
/** Milliseconds of presenter access remaining (never negative). */
export function presenterAccessRemainingMs(state, nowMs) {
    if (!state.expiresAt)
        return PRESENTER_ACCESS_WINDOW_MS;
    return Math.max(0, state.expiresAt - nowMs);
}
//# sourceMappingURL=model.js.map